/**
 * Shared status badge/class helpers.
 *
 * These replace duplicated getStatusClass / getStatusBadgeClass functions
 * across admin and agent views.
 */

import { STATUS_LABELS } from '@core/services/b2bForms.js'

// ---------------------------------------------------------------------------
// Client status
// ---------------------------------------------------------------------------

/**
 * Return a CSS class for a client status value.
 *
 * Handles both English and Hebrew status strings.
 *
 * @param {string|null|undefined} status
 * @returns {string}
 */
export function getClientStatusClass(status) {
  if (!status) return 'status-unknown'
  const s = status.toLowerCase()
  // Check inactive first – 'לא פעיל' also contains 'פעיל'
  if (s === 'inactive' || s.includes('לא פעיל')) return 'status-inactive'
  if (s === 'active' || s.includes('פעיל')) return 'status-active'
  return 'status-other'
}

// ---------------------------------------------------------------------------
// B2B form status
// ---------------------------------------------------------------------------

const B2B_STATUS_CLASSES = {
  New: 'status-new',
  Received: 'status-received',
  InOpening: 'status-in-opening',
  Rejected: 'status-rejected',
  Application: 'status-application',
  Agent: 'status-agent',
  Ordered: 'status-ordered',
  NotOrdered: 'status-not-ordered',
  NotInterested: 'status-not-interested',
}

/**
 * Return a CSS class for a B2B form status.
 *
 * @param {string} status
 * @returns {string}
 */
export function getB2BStatusClass(status) {
  return B2B_STATUS_CLASSES[status] || 'status-default'
}

/**
 * Return the Hebrew display label for a B2B form status.
 *
 * @param {string} status
 * @returns {string}
 */
export function getB2BStatusLabel(status) {
  return STATUS_LABELS[status] || status
}

// ---------------------------------------------------------------------------
// User role
// ---------------------------------------------------------------------------

const USER_ROLE_LABELS = { admin: 'Admin', client: 'Client', agent: 'Agent' }

/**
 * Return a CSS class for a user role badge.
 *
 * @param {string|null} role
 * @returns {string}
 */
export function getUserRoleBadgeClass(role) {
  if (!role) return 'role-none'
  return `role-${role}`
}

/**
 * Return a Hebrew/English display label for a user role.
 *
 * @param {string|null} role
 * @returns {string}
 */
export function getUserRoleLabel(role) {
  if (!role) return 'ללא'
  return USER_ROLE_LABELS[role] || role
}

// ---------------------------------------------------------------------------
// User profile status
// ---------------------------------------------------------------------------

const USER_STATUS_LABELS = { new: 'חדש', active: 'פעיל', inactive: 'לא פעיל', deleted: 'נמחק' }

/**
 * Return a CSS class for a user status badge.
 *
 * @param {string|null} status
 * @returns {string}
 */
export function getUserStatusBadgeClass(status) {
  if (!status) return 'status-none'
  return `status-${status}`
}

/**
 * Return a Hebrew display label for a user profile status.
 *
 * @param {string|null} status
 * @returns {string}
 */
export function getUserStatusLabel(status) {
  if (!status) return '—'
  return USER_STATUS_LABELS[status] || status
}
